import { Trans, useTranslation } from "react-i18next";
import { useAuth } from "../../hooks";
import { useForm } from "react-hook-form";
import React, { useCallback } from "react";
import {
  ModalHeader,
  Modal,
  ModalContent,
  ModalOverlay,
  Heading,
  FormLabel,
  useToast,
  FormControl,
  FormErrorMessage,
  Input,
  Button,
  ModalFooter,
  Text,
} from "@chakra-ui/react";
import { ModalBody } from "amazon-chime-sdk-component-library-react";

/**
 * Renders the confirm sign in modal
 * @param {Object} props The component properties
 * @returns The confirm sign in modal
 */
const ConfirmSignInModal = ({ isOpen, onClose, action, username }) => {
  const { t } = useTranslation();
  const { confirmSignIn, resetPassword } = useAuth();
  const toast = useToast();
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm();

  /**
   * Handles the form submission
   * 
   * @param {Object} values The form values
   */
  const onSubmit = useCallback(
    (values) => {
      return new Promise((resolve) => {
        if (action === "confirmSignIn") {
          confirmSignIn(values)
            .then(() => {
              onClose();
            })
            .catch((error) => {
              if (!toast.isActive(error.message)) {
                toast({
                  id: error.message,
                  title: error.message,
                  status: "error",
                  position: "top",
                  duration: 5000,
                });
              }
            })
            .finally(() => {
              resolve();
            });
        } else {
          resetPassword({ username: username, ...values })
            .then(() => {
              onClose();
            })
            .catch((error) => {
              if (!toast.isActive(error.message)) {
                toast({
                  id: error.message,
                  title: error.message,
                  status: "error",
                  position: "top",
                  duration: 5000,
                });
              }
            })
            .finally(() => {
              resolve();
            });
        }
      });
    },
    [action, confirmSignIn, resetPassword, onClose, toast, username],
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      closeOnOverlayClick={false}
      closeOnEsc={false}
    >
      <ModalOverlay />
      <ModalContent padding={4}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <ModalHeader>
            <Heading>
              {
                action === "confirmSignIn" ? (
                  <Trans>login.confirm.title</Trans>
                ) : (
                  <Trans>login.reset.title</Trans>
                )
              }
            </Heading>
            {
              action === "resetPassword" && (
                <Text fontSize={"medium"} fontWeight={400} marginTop={"8px"}>
                  <Trans>login.reset.description</Trans>
                </Text>
              )
            }
          </ModalHeader>
          <ModalBody>
            {
              action === "resetPassword" && (
                <FormControl isInvalid={errors.confirmationCode}>
                  <FormLabel htmlFor={"confirmationCode"}>
                    <Trans>login.confirmationCode.title</Trans>
                  </FormLabel>
                  <Input
                    backgroundColor={"white"}
                    id={"confirmationCode"}
                    placeholder={t("login.confirmationCode.placeholder")}
                    size={"sm"}
                    type={"text"}
                    {...register("confirmationCode", {
                      required: t("login.confirmationCode.required"),
                    })}
                  />
                  <FormErrorMessage>
                    {errors.confirmationCode && errors.confirmationCode.message}
                  </FormErrorMessage>
                </FormControl>
              )}
            <FormControl isInvalid={errors.newPassword}>
              <FormLabel htmlFor={"newPassword"}>
                <Trans>login.newPassword.title</Trans>
              </FormLabel>
              <Input
                backgroundColor={"white"}
                id={"newPassword"}
                placeholder={t("login.newPassword.placeholder")}
                size={"sm"}
                type={"password"}
                {...register("newPassword", {
                  required: t("login.newPassword.required"),
                })}
              />
              <FormErrorMessage>
                {errors.newPassword && errors.newPassword.message}
              </FormErrorMessage>
            </FormControl>
            <FormControl isInvalid={errors.newPasswordConfirm}>
              <FormLabel htmlFor={"newPasswordConfirm"}>
                <Trans>login.newPasswordConfirm.title</Trans>
              </FormLabel>
              <Input
                backgroundColor={"white"}
                id={"newPasswordConfirm"}
                placeholder={t("login.newPasswordConfirm.placeholder")}
                size={"sm"}
                type={"password"}
                {...register("newPasswordConfirm", {
                  required: t("login.pasnewPasswordConfirmsword.required"),
                  validate: (value, formValues) =>
                    value === formValues.newPassword ||
                    t("login.newPasswordConfirm.doesNotMatch"),
                })}
              />
              <FormErrorMessage>
                {errors.newPasswordConfirm && errors.newPasswordConfirm.message}
              </FormErrorMessage>
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button
              isLoading={isSubmitting}
              size={"md"}
              type={"submit"}
              variant="secondary"
            >
              Confirm
            </Button>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  );
};

export default React.memo(ConfirmSignInModal);
